<template>
  <div class="lf-track-container">
    <div class="header">
      <h2>LF Track</h2>
      <div class="header-actions">
        <el-button type="primary" size="small" @click="openCreateDialog">Create Track</el-button>
        <el-button size="small" @click="fetchData">Refresh</el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="items"
      border
      size="small"
      style="width: 100%"
    >
      <el-table-column prop="track_name" label="Track Name" min-width="220" />
      <el-table-column label="Type" width="120">
        <template slot-scope="scope">{{ scope.row.type || 'Others' }}</template>
      </el-table-column>
      <el-table-column label="Collections" width="150">
        <template slot-scope="scope">
          <span class="collections-count">{{ scope.row.collections_total || 0 }}</span>
          <span class="collections-public"> · {{ scope.row.collections_public || 0 }} public</span>
        </template>
      </el-table-column>
      <el-table-column label="Views" width="90">
        <template slot-scope="scope">{{ scope.row.collections_views || 0 }}</template>
      </el-table-column>
      <el-table-column label="Stars" width="90">
        <template slot-scope="scope">{{ scope.row.collections_stars || 0 }}</template>
      </el-table-column>
      <el-table-column label="Created" width="150">
        <template slot-scope="scope">{{ formatDate(scope.row.create_time) }}</template>
      </el-table-column>
      <el-table-column label="Updated" width="150">
        <template slot-scope="scope">{{ formatDate(scope.row.update_time) }}</template>
      </el-table-column>
      <el-table-column label="Actions" width="280" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="openCollectionsDialog(scope.row)">Collections</el-button>
          <el-button type="primary" size="mini" @click="openEditDialog(scope.row)">Edit</el-button>
          <el-button type="danger" size="mini" @click="handleDelete(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="editingId ? 'Edit Track' : 'Create Track'"
      :visible.sync="showDialog"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="110px" size="small">
        <el-form-item label="Track Name" required>
          <el-input
            v-model="form.track_name"
            placeholder="e.g. Data Science Track"
            maxlength="120"
            show-word-limit
            @keyup.enter.native="submit"
          />
        </el-form-item>
        <el-form-item label="Type" required>
          <el-select v-model="form.type" placeholder="Select a track type" style="width: 100%">
            <el-option
              v-for="type in typeOptions"
              :key="type"
              :label="type"
              :value="type"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showDialog = false">Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">
          {{ editingId ? 'Save' : 'Create' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="collectionsDialogTitle"
      :visible.sync="collectionsDialogVisible"
      width="860px"
      :close-on-click-modal="false"
    >
      <div v-loading="collectionsLoading">
        <div class="collections-summary">
          <span><strong>{{ activeTrack.collections_total || collectionsItems.length }}</strong> collections</span>
          <span><strong>{{ activeTrack.collections_public || 0 }}</strong> public</span>
          <span><strong>{{ activeTrack.collections_views || 0 }}</strong> views</span>
          <span><strong>{{ activeTrack.collections_stars || 0 }}</strong> stars</span>
        </div>
        <el-table :data="collectionsItems" border size="small" max-height="440">
          <el-table-column prop="title" label="Title" min-width="240">
            <template slot-scope="scope">{{ scope.row.title || 'Untitled' }}</template>
          </el-table-column>
          <el-table-column label="Owner" width="170">
            <template slot-scope="scope">{{ scope.row.owner_name || '—' }}</template>
          </el-table-column>
          <el-table-column prop="status" label="Status" width="110" />
          <el-table-column label="Views" width="80">
            <template slot-scope="scope">{{ scope.row.view_count || 0 }}</template>
          </el-table-column>
          <el-table-column label="Stars" width="80">
            <template slot-scope="scope">{{ scope.row.star_count || 0 }}</template>
          </el-table-column>
          <el-table-column label="Updated" width="140">
            <template slot-scope="scope">{{ formatDate(scope.row.update_time) }}</template>
          </el-table-column>
        </el-table>
        <p v-if="!collectionsLoading && !collectionsItems.length" class="collections-empty">
          No collections reference this track yet.
        </p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  listCertTracks,
  createCertTrack,
  updateCertTrack,
  deleteCertTrack,
  listCertTrackCollections
} from '@/apis/lfAdmin'
import dayjs from 'dayjs'

export default {
  name: 'LFTrack',
  data() {
    return {
      loading: false,
      submitting: false,
      items: [],
      showDialog: false,
      editingId: null,
      typeOptions: ['Industry', 'Function', 'Academic', 'Capability', 'Others'],
      form: { track_name: '', type: '' },
      collectionsDialogVisible: false,
      collectionsLoading: false,
      collectionsItems: [],
      activeTrack: {}
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    formatDate(ts) {
      if (!ts) return ''
      return dayjs.unix(ts).format('YYYY-MM-DD HH:mm')
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await listCertTracks()
        if (res.data.code === 0) {
          this.items = res.data.data || []
        } else {
          this.$message.error(res.data.msg || 'Failed to load tracks')
        }
      } catch (e) {
        this.$message.error('Failed to load tracks')
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.editingId = null
      this.form = { track_name: '', type: '' }
      this.showDialog = true
    },
    openCollectionsDialog(row) {
      this.activeTrack = row
      this.collectionsItems = []
      this.collectionsDialogVisible = true
      this.fetchTrackCollections(row)
    },
    async fetchTrackCollections(row) {
      this.collectionsLoading = true
      try {
        const res = await listCertTrackCollections({ id: this._extractId(row) })
        if (res.data.code === 0) {
          this.collectionsItems = res.data.data.items || []
        } else {
          this.$message.error(res.data.msg || 'Failed to load track collections')
        }
      } catch (e) {
        this.$message.error('Failed to load track collections')
        console.error(e)
      } finally {
        this.collectionsLoading = false
      }
    },
    openEditDialog(row) {
      this.editingId = this._extractId(row)
      this.form = {
        track_name: row.track_name || '',
        type: this.typeOptions.includes(row.type) ? row.type : 'Others'
      }
      this.showDialog = true
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`Delete track "${row.track_name}"?`, 'Confirm', { type: 'warning' })
      } catch { return }
      try {
        const res = await deleteCertTrack({ id: this._extractId(row) })
        if (res.data.code === 0) {
          this.$message.success('Deleted')
          this.fetchData()
        } else {
          this.$message.error(res.data.msg || 'Failed to delete')
        }
      } catch (e) {
        this.$message.error('Failed to delete')
      }
    },
    async submit() {
      const name = (this.form.track_name || '').trim()
      if (!name) {
        this.$message.error('Track name is required')
        return
      }
      const type = this.form.type
      if (!this.typeOptions.includes(type)) {
        this.$message.error('Please select a valid track type')
        return
      }
      this.submitting = true
      try {
        const res = this.editingId
          ? await updateCertTrack({ id: this.editingId, track_name: name, type })
          : await createCertTrack({ track_name: name, type })
        if (res.data.code === 0) {
          this.$message.success(this.editingId ? 'Saved' : 'Created')
          this.showDialog = false
          this.fetchData()
        } else {
          this.$message.error(res.data.msg || 'Failed')
        }
      } catch (e) {
        this.$message.error('Failed')
      } finally {
        this.submitting = false
      }
    },
    _extractId(obj) {
      if (!obj) return ''
      if (typeof obj === 'string') return obj
      const id = obj._id
      if (!id) return ''
      if (typeof id === 'string') return id
      return id.$oid || id.$id || (id.toString ? id.toString() : '')
    }
  },
  computed: {
    collectionsDialogTitle() {
      return `Collections — ${this.activeTrack.track_name || ''}`
    }
  }
}
</script>

<style scoped>
.lf-track-container {
  padding: 20px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.collections-count { font-weight: 600; color: #24364b; }
.collections-public { color: #718096; font-size: 12px; }
.collections-summary {
  display: flex;
  gap: 24px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #64758a;
}
.collections-summary strong { color: #24364b; font-weight: 600; margin-right: 3px; }
.collections-empty {
  text-align: center;
  color: #8a97a6;
  font-size: 13px;
  padding: 18px 0;
}
</style>
